import { Link, Navigate, Outlet } from "@tanstack/react-router";
import { ArrowLeftRight, BookOpen, Briefcase, Building2, CalendarClock, FolderTree, Home, LifeBuoy, LogOut, Search, ShieldCheck, Users as UsersIcon, Scale, FileText, Workflow, BarChart3, type LucideIcon } from "lucide-react";
import { useAuth } from "../auth";
import { CallerStateSelect, useCallerState } from "../callerState";
import { useProgramPanel } from "../programPanel";
import { useShortcuts } from "../useShortcuts";
import { ChangePassword } from "../pages/ChangePassword";
import { Footer } from "./Footer";
import { GlobalSearch } from "./GlobalSearch";
import { ProgramPanel } from "./ProgramPanel";

type NavItem = { to: string; label: string; icon: LucideIcon };
export const NAV: NavItem[] = [
  { to: "/", label: "Home", icon: Home },
  { to: "/cases", label: "Cases", icon: Briefcase },
  { to: "/followups", label: "Follow-ups", icon: CalendarClock },
  { to: "/worksheets", label: "Worksheets", icon: FileText },
  { to: "/search", label: "Search", icon: Search },
  { to: "/problems", label: "Problems", icon: LifeBuoy },
  { to: "/directory", label: "Directory", icon: BookOpen },
];
// No label may contain "Edit": the smoke test clicks the program page's "Edit" link by name.
export const ADMIN_NAV: NavItem[] = [
  { to: "/admin/verification", label: "Verification", icon: ShieldCheck },
  { to: "/admin/departments", label: "Departments", icon: Building2 },
  { to: "/admin/categories", label: "Categories", icon: FolderTree },
  { to: "/admin/problems", label: "Problems KB", icon: LifeBuoy },
  { to: "/admin/synonyms", label: "Synonyms", icon: ArrowLeftRight },
  { to: "/admin/intent-rules", label: "Intent rules", icon: Workflow },
  { to: "/admin/rules", label: "Eligibility rules", icon: Scale },
  { to: "/admin/templates", label: "Templates", icon: FileText },
  { to: "/admin/users", label: "Users", icon: UsersIcon },
];

// Supervisors and admins; the Reports page is gated by ManagerOnly.
export const MANAGER_NAV: NavItem[] = [{ to: "/admin/reports", label: "Reports", icon: BarChart3 }];

/**
 * A drawer over the right edge below lg; docked beside the page from lg, at most 28rem and 40% of the screen. The
 * drawer stays under the top bar (z-30), so the search suggestions open over it.
 */
export const PANEL_PLACEMENT = "fixed bottom-0 right-0 top-14 z-20 w-[28rem] max-w-[calc(100vw-2rem)] shadow-xl "
  + "lg:sticky lg:bottom-auto lg:right-auto lg:z-auto lg:h-[calc(100vh-3.5rem)] lg:w-[min(28rem,40vw)] lg:shrink-0 lg:shadow-none";

function NavLink({ item }: { item: NavItem }) {
  const Icon = item.icon;
  return (
    <Link to={item.to as "/"} activeOptions={{ exact: item.to === "/" }}
      className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
      activeProps={{ className: "bg-brand-50 font-semibold text-brand-700 dark:bg-slate-800 dark:text-white" }}>
      <Icon size={16} /> {item.label}
    </Link>
  );
}

export function Layout() {
  const { me, loading, signOut } = useAuth();
  const caller = useCallerState();
  const panel = useProgramPanel();
  useShortcuts();
  if (loading) return <div className="p-8 text-sm text-slate-500">Loading…</div>;
  if (!me) return <Navigate to="/login" />;
  // The server refuses everything else until a temporary password is replaced; show nothing else either.
  if (me.mustChangePassword) return <ChangePassword />;
  return (
    <div className="flex min-h-screen">
      <aside className="flex w-56 shrink-0 flex-col border-r border-slate-200 bg-white px-3 py-4 dark:border-slate-800 dark:bg-slate-900">
        <div className="mb-6 px-2 text-sm font-bold">Ask For Helping Hand<div className="text-xs font-normal text-slate-500">Rep portal</div></div>
        <nav className="flex flex-col gap-0.5">{NAV.map((i) => <NavLink key={i.to} item={i} />)}</nav>
        {me.user.role === "supervisor" && (
          <nav className="mt-6 flex flex-col gap-0.5">{MANAGER_NAV.map((i) => <NavLink key={i.to} item={i} />)}</nav>
        )}
        {me.user.role === "admin" && ADMIN_NAV.length > 0 && (
          <>
            <div className="label mt-6 px-2">Admin</div>
            <nav className="flex flex-col gap-0.5">{[...ADMIN_NAV, ...MANAGER_NAV].map((i) => <NavLink key={i.to} item={i} />)}</nav>
          </>
        )}
        <div className="mt-auto border-t border-slate-200 pt-3 text-xs dark:border-slate-800">
          <div className="px-2 font-medium">{me.user.name}</div>
          <div className="px-2 text-slate-500">{me.user.role}</div>
          <button className="btn-ghost mt-2 w-full" onClick={() => { caller.setState(undefined); panel.close(); signOut(); }}><LogOut size={14} /> Sign out</button>
        </div>
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex flex-wrap items-center gap-3 border-b border-slate-200 bg-white px-6 py-2 dark:border-slate-800 dark:bg-slate-900">
          <div className="min-w-0 flex-1"><GlobalSearch /></div>
          <CallerStateSelect />
        </header>
        {/* The open program docks beside the page (non-modal): the page stays usable while the rep reads it. Below lg
            (a tablet) there is no room beside the page, so it overlays the right edge as a drawer, still without a backdrop. */}
        <div className="flex flex-1 items-start">
          <main className="min-w-0 flex-1 p-6"><Outlet /></main>
          {panel.current && (
            <ProgramPanel slug={panel.current.slug} state={panel.current.state} onClose={panel.close}
              onStateChange={(s) => panel.open(panel.current!.slug, s)}
              className={PANEL_PLACEMENT} />
          )}
        </div>
        <Footer />
      </div>
    </div>
  );
}
